import { Injectable } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { FormGroup, FormArray } from '@angular/forms';

import { Observable } from 'rxjs';
import days from 'dayjs';

import { environment } from '../../environments/environment';
import { Partner, Event } from '@app/shared/_models/common';
import { LoginService } from '@app/common/login.service';
import { ObjectType, EventType, jsonheader, iso8601formatTime } from '@app/shared/constants';
import { pickedDateStr } from '@app/shared/time';

export interface DeliveryPlanning {
  id: number;             // delivery id
  id_order: number;
  mode: number;
  askdatdel: string | null;
  askslot: number | null;
  askop: number | null;
  realdatdel?: string | null;    // non nul = BL signé (immutable)
  id_del_run?: number | null;    // FK del_run — tournée affectée
  seq_run?: number | null;       // ordre de passage dans la tournée
  duration?: number | null;      // durée estimée sur place (secondes)
  start_time?: string | null;    // heure de passage planifiée
  // Les quatre temps d'une livraison, à ne jamais confondre (voir delrun-schedule.ts) :
  //   askdatdel/askop/askslot = la DEMANDE du client   |  lockdatdel = la CONTRAINTE du planificateur
  //   plandatdel = l'ENGAGEMENT figé à la validation   |  realdatdel = le RÉEL (BL signé)
  lockdatdel?: string | null;    // CONTRAINTE : heure d'arrivée verrouillée à la main (ISO) — seule entrée contraignante du calcul
  plandatdel?: string | null;    // ENGAGEMENT : heure d'arrivée figée à la validation de la tournée (ISO) — jamais relue comme contrainte
  lnk_order: {
    id: number;           // order id (routing vers /sales/quotation/:id, /rental/:id pour une LOCATION)
    doc_type?: string | null;   // LOCATION : commande → page du dossier, BL → fenêtre de livraison location
    addr_deliv: number;
    lnk_customer: Partner | null;
    lnk_partner_deliv?: Partner | null;   // adresse de livraison résolue (renvoyée par le back)
    lines: { id: number; template: number; qty: number; product_name: string }[] | null;
  };
}



// Limite demandée pour les BL d'une commande ; le serveur coupe à 100 quand il saute ce filtre (id_order lu avant limit, ordre de HashMap).
export const ORDER_BLS_LIMIT = 1000;
export const SERVER_DEFAULT_LIMIT = 100;

@Injectable({
  providedIn: 'root'
})
export class LogisticService {

  constructor(private http: HttpClient, private loginService: LoginService) { }

  // Migré depuis QuotationService : POST d'un event livraison (INSERT/UPDATE/DELETE).
  postDeliveryEvent(eventtyp: number, formGroup: FormGroup): Observable<any> {
    this.beforeSubmit(formGroup);    // Do some type adjustements
    let event: Event = {
      id: 0,
      typ: eventtyp,
      userid: this.loginService.loggeduser?.id,
      objectid: formGroup.get('id')?.value,
      objecttyp: ObjectType.DELIVERY,        // Delivery
      data: formGroup.value,
    };
    return this.http.post<any>(environment.server_url + "/event", event, { 'headers': jsonheader });
  }

  // Impression du BL de la livraison active : POST /event de type PRINT (objet DELIVERY).
  // Le back met à jour la livraison (lignes = qtés livrées) puis renvoie le BL rendu en HTML (data.html).
  // deliveryFormGroup peut être null/vide : si le BL est déjà signé, on n'envoie pas les lignes (option B),
  // seul objectid (id de la livraison) est transmis. Le back renvoie alors le PDF d'archivage signé (data.signed, data.pdf_base64_signed).
  postDeliveryPrintEvent$(deliveryFormGroup: FormGroup | null): Observable<any> {
    const event: Event = {
      id: 0,
      typ: EventType.DELIVERY_VALIDATE_AND_PRINT,
      userid: this.loginService.loggeduser?.id,
      objectid: deliveryFormGroup?.get('id')?.value ?? null,
      objecttyp: ObjectType.DELIVERY,
      data: deliveryFormGroup ? deliveryFormGroup.value : null,
    };
    return this.http.post<any>(environment.server_url + "/event", event, { 'headers': jsonheader });
  }

  // Comportement 2 (BL signé) — GET pur : renvoie le PDF d'archivage signé de la livraison.
  // Aucun event, aucune écriture. 404 {"error":"no_signed_bl"} si pas de BL signé (legacy / non réalisée).
  getSignedBl$(deliveryId: number): Observable<Blob> {
    return this.http.get(environment.server_url + "/deliveries/" + deliveryId + "/signed-bl", { responseType: 'blob' });
  }

  // Validation de la récupération (BL signé) : envoie la livraison (lignes = qtés livrées) + la signature PNG base64
  // en un seul POST /event. Le back (process_delivery_validate, event 401) resync les lignes, stampe realdatdel = now()
  // et stocke la signature en c_attach.
  postDeliveryValidation(deliveryFormGroup: FormGroup, signature: string, note = ''): Observable<any> {
    const event: Event = {
      id: 0,
      typ: EventType.DELIVERY_SIGN,
      userid: this.loginService.loggeduser?.id,
      objectid: deliveryFormGroup.get('id')?.value,
      objecttyp: ObjectType.DELIVERY,
      data: { ...deliveryFormGroup.value, signature, signature_note: note },  // la remarque n'a pas de colonne : data de l'event + PDF archivé
    };
    return this.http.post<any>(environment.server_url + '/event', event, { headers: jsonheader });
  }

  // Migré depuis QuotationService. Prépare le FormGroup avant POST (casts int, formats de dates).
  // Appelé avec des FormGroups de LIVRAISON, mais aussi de COMMANDE (via QuotationService.beforeSubmit) :
  // chaque conversion ne s'applique que si le contrôle existe.
  // NB : le bloc lnk_receivables est un concept commande (flux quotation) — à re-splitter côté sales à terme.
  beforeSubmit(fg: FormGroup) {
    // Delivery : les <select>/<toggle> renvoient des strings, le back attend des int (ou null si non renseigné)
    this.patchIntOrNull(fg, 'askslot');
    this.patchIntOrNull(fg, 'planslot');
    this.patchIntOrNull(fg, 'askop');
    this.patchIntOrNull(fg, 'id_planning');
    this.patchIntOrNull(fg, 'duration');		// temps sur place (secondes)

    const modeControl = fg.get('mode');
    if (modeControl) modeControl.patchValue(+modeControl.value);    // Cast mode back to int

    const askdatdelControl = fg.get('askdatdel');
    if (askdatdelControl) {   // Datepicker ou 'YYYY-MM-DD' -> jour calendaire nu (une DATE, pas un instant).
      // pickedDateStr et non new Date(v) : new Date('YYYY-MM-DD') = minuit UTC, qui redonnait J-1
      // dans les fuseaux à l'ouest ; null si absent (sinon new Date(null) = 01/01/1970).
      askdatdelControl.patchValue(pickedDateStr(askdatdelControl.value));
    }

    if (!fg.get('plandatdel')?.value) fg.get('plandatdel')?.patchValue(null);
    if (!fg.get('realdatdel')?.value) fg.get('realdatdel')?.patchValue(null);

    // TODO check this out. receivable refactoring
    const lnk_receivables = fg.value.lnk_receivables;
    if (lnk_receivables) {
      for (let i = 0; i < lnk_receivables.length; i++) {
        const lnk_receivable_payments = lnk_receivables[i].lnk_receivable_payments;
        if (lnk_receivable_payments) {
          for (let j = 0; j < lnk_receivable_payments.length; j++) {
            if (lnk_receivable_payments[j].lnk_payment && lnk_receivable_payments[j].lnk_payment.dat) {
              let isodate = days(new Date(lnk_receivable_payments[j].lnk_payment.dat)).format(iso8601formatTime);
              ((fg.get('lnk_receivables') as FormArray)?.at(i).get('lnk_receivable_payments') as FormArray)?.at(j).get('lnk_payment')?.get('dat')?.patchValue(isodate);
            }
          }
        }
        if (lnk_receivables[i].due_date) {
          let isodate = days(new Date(lnk_receivables[i].due_date)).format(iso8601formatTime);
          (fg.get('lnk_receivables') as FormArray)?.at(i).get('due_date')?.patchValue(isodate);
        }
      }
    }
  }

  // 0, '0', null, '' ou valeur non numérique => null ; sinon cast int (les <select> renvoient des strings)
  private patchIntOrNull(fg: FormGroup, name: string): void {
    const control = fg.get(name);
    if (!control) return;
    const n = parseInt(control.value, 10);
    control.patchValue(Number.isFinite(n) && n !== 0 ? n : null);
  }


  getPlanedDeliveriesByDate(dat: Date): Observable<DeliveryPlanning[]> {
    const datstr = pickedDateStr(dat);    // jour du sélecteur (toISOString décalait d'un jour avant l'heure UTC)
    // Sans limit:, le serveur coupe à 100 BL : une tournée montrait une partie de ses BL, et « Valider » libérait les autres.
    return this.http.get<DeliveryPlanning[]>(environment.server_url + "/deliveries?q=datstart:" + datstr + " AND datend:" + datstr + " AND limit:2000");
  }

  // BL d'un dossier, tournée comprise, du plus récent au plus ancien ; limite large demandée (#57), mais le serveur ne la lit pas toujours avec id_order (100 BL alors).
  getDeliveriesOfOrder$(orderId: number): Observable<any[]> {
    return this.http.get<any[]>(environment.server_url + "/deliveries?q=id_order:" + orderId + " AND limit:" + ORDER_BLS_LIMIT + "&o=lines,attachs");
  }



}
